import { useCurrentFacility } from '@/hooks/useCurrentFacility'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Building2, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { usePermission } from '@/lib/permissions'
import { useCapacitySummary } from '@/hooks/useStorage'
import { useFacilityLocations, useFacilityMutations } from '@/hooks/useFacility'
import { FacilityExplorer, findUnit } from './FacilityExplorer'
import { FacilityForm, useFacilityExample } from './FacilityBar'
import { FacilitySwitcher } from './FacilitySwitcher'

/**
 * The facility set-up (z8nrz7fw25), as one screen: the facility at the top,
 * then `FacilityExplorer` — buildings and floors down the side, the floor in
 * 3D with its rooms below it, and the chosen room on the right.
 *
 * Opened from a storage unit (`focusUnitId`), it lands on that unit's room and
 * marks the unit. Someone without `equipment.manage` sees the same screen to
 * look around, with every add, rename and remove control left out.
 */
export function FacilityStudio({
  open,
  onOpenChange,
  focusUnitId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  focusUnitId?: string | null
}) {
  const { can } = usePermission()
  const editable = can('equipment.manage')
  const locations = useFacilityLocations(open)
  const capacity = useCapacitySummary(open)
  // One facility choice for the whole app (z8nrz7fw25): switching it here switches it everywhere.
  const [facilityId, setFacilityId] = useCurrentFacility()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) setError(null)
  }, [open])

  const tree = locations.data
  // Opened on a unit: start in the facility that unit is in — once per
  // opening. Not on every reload of the tree: editing another facility reloads
  // it, and that must not pull the view back to the unit's facility.
  const landedOn = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      landedOn.current = null
      return
    }
    if (!tree || !focusUnitId || landedOn.current === focusUnitId) return
    const at = findUnit(tree, focusUnitId)
    if (at?.facilityId) setFacilityId(at.facilityId)
    landedOn.current = focusUnitId
  }, [open, tree, focusUnitId, setFacilityId])

  const facility = tree?.facilities.find((f) => f.id === facilityId) ?? tree?.facilities[0] ?? null
  const byEquipment = useMemo(
    () => new Map((capacity.data ?? []).filter((r) => r.equipmentId).map((r) => [r.equipmentId as string, r])),
    [capacity.data],
  )

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That change was not saved.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex h-[92vh] max-w-[1440px] flex-col gap-0 overflow-hidden p-0'>
        <header className='flex flex-wrap items-center gap-3 border-b px-5 py-3 pr-12'>
          <div className='flex items-center gap-2'>
            <span className='flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary'>
              <Building2 className='h-5 w-5' />
            </span>
            <div>
              <DialogTitle className='text-base'>{editable ? 'Facility set-up' : 'Facility'}</DialogTitle>
              <DialogDescription className='text-xs'>Where every unit is: facility, building, floor and room.</DialogDescription>
            </div>
          </div>
          {tree && tree.facilities.length > 0 && (
            <FacilitySwitcher facilities={tree.facilities} selected={facility} onSelect={setFacilityId} editable={editable} run={run} className='md:ml-6' />
          )}
        </header>

        {error && (
          <p role='alert' className='border-b border-destructive/20 bg-destructive/5 px-5 py-2 text-xs text-destructive'>
            {error}
          </p>
        )}

        {!tree ? (
          <div className='flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground'>
            {locations.isError ? (
              'Locations could not be loaded.'
            ) : (
              <>
                <Loader2 className='h-4 w-4 animate-spin' /> Loading…
              </>
            )}
          </div>
        ) : !facility ? (
          editable ? (
            <FirstFacility onCreated={setFacilityId} run={run} />
          ) : (
            <p className='flex flex-1 items-center justify-center text-sm text-muted-foreground'>No facility is set up yet.</p>
          )
        ) : (
          <FacilityExplorer
            key={facility.id}
            tree={tree}
            facility={facility}
            byEquipment={byEquipment}
            focusUnitId={focusUnitId}
            editable={editable}
            run={run}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

/** The first step on an empty site: name the facility, and the rest follows on the drawing. */
function FirstFacility({ onCreated, run }: { onCreated: (id: string) => void; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const mut = useFacilityMutations()
  const example = useFacilityExample()
  return (
    <div className='flex flex-1 items-center justify-center bg-slate-50/70 p-6'>
      <div className='w-full max-w-sm space-y-3 text-center'>
        <span className='mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary'>
          <Building2 className='h-6 w-6' />
        </span>
        <h3 className='text-base font-semibold text-slate-900'>Start with your facility</h3>
        <p className='text-sm text-muted-foreground'>
          The site you run, for example {example.code} for {example.name}. You set it up once; buildings, floors and rooms go inside it.
        </p>
        <div className='text-left'>
          <FacilityForm
            submitLabel='Create facility'
            onSubmit={(v) => run(async () => onCreated((await mut.createFacility.mutateAsync(v)).id))}
          />
        </div>
      </div>
    </div>
  )
}
